import React, { useEffect } from 'react';
import { Navigate, NavigateProps } from 'react-router-dom';
import {
  FlowRedirectContext,
  FlowRedirectReason,
  trackFlowRedirect,
} from 'utilities/paymentFlowTelemetry';

export type TrackedNavigateProps = Pick<NavigateProps, 'to' | 'replace'> & {
  /** Why the flow is redirecting. Becomes the Sentry issue grouping. */
  reason: FlowRedirectReason;
  /** Everything we know about the flow state at the moment of the decision. */
  context: Omit<FlowRedirectContext, 'toPath'>;
};

const toPath = (to: NavigateProps['to']): string =>
  typeof to === 'string' ? to : `${to.pathname ?? ''}${to.search ?? ''}`;

/**
 * Drop-in replacement for react-router's <Navigate> that records *why* the
 * flow redirected before it does so. Fires exactly once per mount so one
 * redirect decision produces one telemetry event.
 */
const TrackedNavigate = ({
  to,
  replace,
  reason,
  context,
}: TrackedNavigateProps): JSX.Element => {
  useEffect(() => {
    trackFlowRedirect(reason, { ...context, toPath: toPath(to) });
    // Intentionally mount-only: a redirect decision is a single event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <Navigate to={to} replace={replace} />;
};

export default TrackedNavigate;
